// Import shared constants
import { STORAGE_KEYS, getUserKey, getApiEndpoint, vertexFetch, storageSet, storageRemove } from './constants.js';

// Re-export the functions from constants.js for backwards compatibility
export { getUserKey, getApiEndpoint };

/**
 * ============================================================================
 * STORAGE FUNCTIONS
 * ============================================================================
 */

export async function saveUserKey(userKey) {
  await storageSet(STORAGE_KEYS.USER_KEY, userKey);
}

export async function removeUserKey() {
  await storageRemove(STORAGE_KEYS.USER_KEY);
}

/**
 * ============================================================================
 * SETTINGS UI HANDLERS
 * ============================================================================
 */

export function showScreen(screenName) {
  const promptScreen = document.getElementById('promptScreen');
  const enterKeyScreen = document.getElementById('enterKeyScreen');
  const createKeyScreen = document.getElementById('createKeyScreen');
  const savedScreen = document.getElementById('savedScreen');
  const configSection = document.getElementById('configSection');

  // Hide all screens
  promptScreen.classList.add('hidden');
  enterKeyScreen.classList.add('hidden');
  createKeyScreen.classList.add('hidden');
  savedScreen.classList.add('hidden');

  // Show/hide config section (only exists in some pages)
  if (configSection) {
    if (screenName === 'saved') {
      configSection.classList.remove('hidden');
    } else {
      configSection.classList.add('hidden');
    }
  }

  const existingKeyInput = document.getElementById('existingKeyInput');
  const enterKeyError = document.getElementById('enterKeyError');
  const loadingKeySpinner = document.getElementById('loadingKeySpinner');
  const keyCreated = document.getElementById('keyCreated');
  const createKeyError = document.getElementById('createKeyError');
  const backFromCreateBtn = document.getElementById('backFromCreateBtn');
  const keyDisplay = document.getElementById('keyDisplay');

  // Show selected screen
  switch(screenName) {
    case 'prompt':
      promptScreen.classList.remove('hidden');
      break;
    case 'enterKey':
      enterKeyScreen.classList.remove('hidden');
      existingKeyInput.value = '';
      enterKeyError.textContent = '';
      existingKeyInput.focus();
      break;
    case 'createKey':
      createKeyScreen.classList.remove('hidden');
      loadingKeySpinner.classList.remove('hidden');
      keyCreated.classList.add('hidden');
      createKeyError.textContent = '';
      backFromCreateBtn.classList.add('hidden');
      break;
    case 'saved':
      savedScreen.classList.remove('hidden');
      keyDisplay.classList.add('hidden');
      break;
  }
}

export async function handleCreateKey() {
  showScreen('createKey');

  const displayedKey = document.getElementById('displayedKey');
  const loadingKeySpinner = document.getElementById('loadingKeySpinner');
  const keyCreated = document.getElementById('keyCreated');
  const createKeyError = document.getElementById('createKeyError');
  const backFromCreateBtn = document.getElementById('backFromCreateBtn');

  try {
    const apiEndpoint = await getApiEndpoint();
    // Include credentials so server can set auth cookie
    const response = await vertexFetch(`${apiEndpoint}/user/key`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Server returns { userKey, userAlias } and sets auth cookie
    const result = await response.json();
    const userKey = result.userKey || result; // Handle both old and new response format

    // Store alias for display, not the key (cookie handles auth)
    if (result.userAlias) {
      await storageSet(STORAGE_KEYS.USER_ALIAS, result.userAlias);
    }

    displayedKey.textContent = userKey;
    loadingKeySpinner.classList.add('hidden');
    keyCreated.classList.remove('hidden');

  } catch (error) {
    console.error('Error creating user key:', error);
    loadingKeySpinner.classList.add('hidden');
    createKeyError.textContent = `Error: ${error.message}. Please check your API endpoint configuration.`;
    backFromCreateBtn.classList.remove('hidden');
  }
}

export async function handleSaveExistingKey() {
  const existingKeyInput = document.getElementById('existingKeyInput');
  const enterKeyError = document.getElementById('enterKeyError');

  const userKey = existingKeyInput.value.trim();

  if (!userKey) {
    enterKeyError.textContent = 'Please enter a valid key.';
    return;
  }

  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidPattern.test(userKey)) {
    enterKeyError.textContent = 'Invalid key format. Expected a UUID format.';
    return;
  }

  try {
    const apiEndpoint = await getApiEndpoint();
    // Validate key and set auth cookie via /user/login
    // Uses the login endpoint which ignores stale cookies
    const response = await vertexFetch(`${apiEndpoint}/user/login`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userKey}`
      },
      credentials: 'include',
      cache: 'no-store'
    });

    if (!response.ok) {
      enterKeyError.textContent = 'Invalid key. Please check and try again.';
      return;
    }

    // Store alias for display, not the key (cookie handles auth)
    const result = await response.json();
    if (result.userAlias) {
      await storageSet(STORAGE_KEYS.USER_ALIAS, result.userAlias);
    }

    showScreen('saved');
  } catch (error) {
    console.error('Error validating key:', error);
    enterKeyError.textContent = 'Error validating key. Please try again.';
  }
}

export async function toggleKeyDisplay() {
  // With cookie-based auth, we no longer store/display the key
  // This function is kept for backwards compatibility but does nothing meaningful
  const keyDisplay = document.getElementById('keyDisplay');
  const viewKeyBtn = document.getElementById('viewKeyBtn');
  const keyValidationStatus = document.getElementById('keyValidationStatus');

  if (keyDisplay) {
    if (keyDisplay.classList.contains('hidden')) {
      keyDisplay.classList.remove('hidden');
      if (viewKeyBtn) viewKeyBtn.textContent = 'Hide Status';
      await checkAuthStatus();
    } else {
      keyDisplay.classList.add('hidden');
      if (keyValidationStatus) keyValidationStatus.classList.add('hidden');
      if (viewKeyBtn) viewKeyBtn.textContent = 'View Status';
    }
  }
}

export async function checkAuthStatus() {
  const keyValidationStatus = document.getElementById('keyValidationStatus');

  if (!keyValidationStatus) {
    return;
  }

  try {
    const apiEndpoint = await getApiEndpoint();
    // Check auth via cookie
    const response = await vertexFetch(`${apiEndpoint}/user/key/check`, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store'
    });

    keyValidationStatus.classList.remove('hidden');

    if (response.ok) {
      keyValidationStatus.classList.remove('invalid');
      keyValidationStatus.classList.add('valid');
      keyValidationStatus.title = 'Authenticated';
    } else {
      keyValidationStatus.classList.remove('valid');
      keyValidationStatus.classList.add('invalid');
      keyValidationStatus.title = 'Not authenticated';
    }
  } catch (error) {
    console.error('[Vertex] Error checking auth status:', error);
    keyValidationStatus.classList.remove('hidden', 'valid');
    keyValidationStatus.classList.add('invalid');
    keyValidationStatus.title = 'Error checking authentication';
  }
}

// Legacy alias for backwards compatibility
export const validateUserKey = checkAuthStatus;

export async function handleRemoveKey() {
  if (confirm('Are you sure you want to log out? You will need to enter your key again.')) {
    // Clear local alias
    await storageRemove(STORAGE_KEYS.USER_ALIAS);
    // Note: The auth cookie is HttpOnly so we can't clear it from JS
    // The user will need to clear cookies manually or wait for expiry
    // TODO: Add server-side logout endpoint that clears the cookie
    showScreen('prompt');
  }
}

export async function copyWithFeedback(btn, text) {
  await navigator.clipboard.writeText(text);
  btn.style.color = '#28a745';
  btn.style.borderColor = '#28a745';
  setTimeout(() => {
    btn.style.color = '';
    btn.style.borderColor = '';
  }, 1500);
}

/**
 * ============================================================================
 * SHARED ICON DATA
 * ============================================================================
 * Path data shared between icons.tsx (JSX) and DOM builders (content.js, etc.)
 */

export const SET_ICON_PATH_D = 'M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z';

export function setIconSvgHtml(size = 12, strokeWidth = 1.5) {
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" ' +
    'width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="' + strokeWidth + '" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="' + SET_ICON_PATH_D + '" />' +
    '</svg>'
  );
}
